import { useState, useEffect, useCallback } from "react";
import {
  Form,
  useLoaderData,
  useActionData,
  useNavigation,
  useFetcher,
  useRevalidator,
} from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button, LinkButton } from "../../components/common";
import type { ApiKeysData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import type { ApiKeyResponse } from "../../types/api";
import { API_KEY_SCOPES } from "../../types/api";
import styles from "./ApiKeysPage.module.css";

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function ScopeLabel({ scope }: { scope: string }) {
  return (
    <span
      className={`${styles.scope} ${styles[`scope${scope.charAt(0).toUpperCase()}${scope.slice(1)}`]}`}
    >
      {scope}
    </span>
  );
}

interface KeyDisplayModalProps {
  rawKey: string;
  onClose: () => void;
}

function KeyDisplayModal({ rawKey, onClose }: KeyDisplayModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(rawKey);
      setCopied(true);
      toast.success("API key copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  }, [rawKey]);

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="presentation"
    >
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <h3>API Key Created</h3>
        <p className={styles.modalWarning}>
          Copy your API key now. You won't be able to see it again.
        </p>
        <div className={styles.keyDisplay}>
          <code className={styles.keyValue}>{rawKey}</code>
          <Button type="button" variant="secondary" size="sm" onClick={handleCopy}>
            {copied ? "Copied!" : "Copy"}
          </Button>
        </div>
        <div className={styles.modalActions}>
          <Button type="button" variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

interface EditKeyFormProps {
  apiKey: ApiKeyResponse;
  orgId: string;
  onCancel: () => void;
}

function EditKeyForm({ apiKey, orgId, onCancel }: EditKeyFormProps) {
  const fetcher = useFetcher();
  const isSubmitting = fetcher.state === "submitting";

  useEffect(() => {
    if (fetcher.data) {
      const result = fetcher.data as ActionResult;
      if (result.success) {
        toast.success("API key updated");
        onCancel();
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [fetcher.data, onCancel]);

  return (
    <fetcher.Form
      method="post"
      action={`/organizations/${orgId}/settings/api-keys/${apiKey.id}/edit`}
      className={styles.editForm}
    >
      <div className={styles.formGroup}>
        <label htmlFor={`edit-name-${apiKey.id}`}>Name</label>
        <input
          id={`edit-name-${apiKey.id}`}
          name="name"
          type="text"
          defaultValue={apiKey.name}
          disabled={isSubmitting}
        />
      </div>
      <div className={styles.formGroup}>
        <span className={styles.scopesLabel}>Scopes</span>
        <div className={styles.scopeCheckboxes}>
          {API_KEY_SCOPES.map((scope) => (
            <label key={scope} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="scopes"
                value={scope}
                defaultChecked={apiKey.scopes.includes(scope)}
                disabled={isSubmitting}
              />
              {scope}
            </label>
          ))}
        </div>
      </div>
      <div className={styles.editActions}>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          loading={isSubmitting}
          loadingText="Saving..."
        >
          Save
        </Button>
      </div>
    </fetcher.Form>
  );
}

export default function ApiKeysPage() {
  const { organization, apiKeys } = useLoaderData() as ApiKeysData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const revalidator = useRevalidator();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [rawKeyDisplay, setRawKeyDisplay] = useState<string | null>(null);
  const [editingKeyId, setEditingKeyId] = useState<string | null>(null);
  const [revokeConfirmId, setRevokeConfirmId] = useState<string | null>(null);
  const [rotateConfirmId, setRotateConfirmId] = useState<string | null>(null);

  const revokeFetcher = useFetcher();
  const rotateFetcher = useFetcher();

  const isCreating = navigation.state === "submitting";
  const isRevoking = revokeFetcher.state === "submitting";
  const isRotating = rotateFetcher.state === "submitting";

  // Handle create action result
  useEffect(() => {
    if (!actionData) return;

    if (actionData.success && actionData.data) {
      const { rawKey } = actionData.data as { rawKey: string };
      setRawKeyDisplay(rawKey);
      setShowCreateForm(false);
      toast.success("API key created");
    } else if (actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  // Handle revoke result
  useEffect(() => {
    if (revokeFetcher.data) {
      const result = revokeFetcher.data as ActionResult;
      if (result.success) {
        toast.success("API key revoked");
        setRevokeConfirmId(null);
        revalidator.revalidate();
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [revokeFetcher.data, revalidator]);

  // Handle rotate result
  useEffect(() => {
    if (rotateFetcher.data) {
      const result = rotateFetcher.data as ActionResult;
      if (result.success && result.data) {
        const { rawKey } = result.data as { rawKey: string };
        setRawKeyDisplay(rawKey);
        setRotateConfirmId(null);
        toast.success("API key rotated");
        revalidator.revalidate();
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [rotateFetcher.data, revalidator]);

  const activeKeys = apiKeys.filter((k) => !k.revokedAt);
  const revokedKeys = apiKeys.filter((k) => k.revokedAt);

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.container}>
          <div className={styles.header}>
            <h1>API Keys</h1>
            <LinkButton to={`/organizations/${organization.id}/settings`} variant="secondary">
              Back to Settings
            </LinkButton>
          </div>

          {/* Create Key Section */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Create API Key</h2>
              {!showCreateForm && (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setShowCreateForm(true)}
                >
                  New Key
                </Button>
              )}
            </div>

            {showCreateForm && (
              <Form method="post" className={styles.createForm}>
                <div className={styles.formGroup}>
                  <label htmlFor="key-name">Name</label>
                  <input
                    id="key-name"
                    name="name"
                    type="text"
                    placeholder="e.g. Production API Key"
                    disabled={isCreating}
                  />
                  {actionData?.fieldErrors?.name && (
                    <span className={styles.formError}>{actionData.fieldErrors.name}</span>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <span className={styles.scopesLabel}>Scopes</span>
                  <div className={styles.scopeCheckboxes}>
                    {API_KEY_SCOPES.map((scope) => (
                      <label key={scope} className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          name="scopes"
                          value={scope}
                          defaultChecked={scope === "read"}
                          disabled={isCreating}
                        />
                        {scope}
                      </label>
                    ))}
                  </div>
                  {actionData?.fieldErrors?.scopes && (
                    <span className={styles.formError}>{actionData.fieldErrors.scopes}</span>
                  )}
                </div>
                <div className={styles.formActions}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowCreateForm(false)}
                    disabled={isCreating}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isCreating}
                    loadingText="Creating..."
                  >
                    Create Key
                  </Button>
                </div>
              </Form>
            )}

            {!showCreateForm && (
              <p className={styles.sectionDescription}>
                API keys allow programmatic access to your organization's resources. Keys are shown
                only once at creation.
              </p>
            )}
          </div>

          {/* Active Keys */}
          <div className={styles.section}>
            <h2>Active Keys</h2>
            {activeKeys.length === 0 ? (
              <p className={styles.emptyState}>No active API keys.</p>
            ) : (
              <div className={styles.keyList}>
                {activeKeys.map((apiKey) => (
                  <div key={apiKey.id} className={styles.keyCard}>
                    {editingKeyId === apiKey.id ? (
                      <EditKeyForm
                        apiKey={apiKey}
                        orgId={organization.id}
                        onCancel={() => setEditingKeyId(null)}
                      />
                    ) : (
                      <>
                        <div className={styles.keyInfo}>
                          <div className={styles.keyHeader}>
                            <span className={styles.keyName}>{apiKey.name}</span>
                            <code className={styles.keyPrefix}>{apiKey.keyPrefix}...</code>
                          </div>
                          <div className={styles.keyMeta}>
                            <span>Created {formatDate(apiKey.createdAt)}</span>
                            {apiKey.lastUsedAt && (
                              <span>Last used {formatDateTime(apiKey.lastUsedAt)}</span>
                            )}
                          </div>
                          <div className={styles.keyScopes}>
                            {apiKey.scopes.map((scope) => (
                              <ScopeLabel key={scope} scope={scope} />
                            ))}
                          </div>
                        </div>
                        <div className={styles.keyActions}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingKeyId(apiKey.id)}
                          >
                            Edit
                          </Button>

                          {rotateConfirmId === apiKey.id ? (
                            <div className={styles.inlineConfirm}>
                              <span className={styles.confirmLabel}>
                                Rotate? Old key will stop working.
                              </span>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setRotateConfirmId(null)}
                                disabled={isRotating}
                              >
                                No
                              </Button>
                              <Button
                                type="button"
                                variant="accent"
                                size="sm"
                                loading={isRotating}
                                loadingText="..."
                                onClick={() =>
                                  rotateFetcher.submit(null, {
                                    method: "post",
                                    action: `/organizations/${organization.id}/settings/api-keys/${apiKey.id}/rotate`,
                                  })
                                }
                              >
                                Yes
                              </Button>
                            </div>
                          ) : (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => setRotateConfirmId(apiKey.id)}
                            >
                              Rotate
                            </Button>
                          )}

                          {revokeConfirmId === apiKey.id ? (
                            <div className={styles.inlineConfirm}>
                              <span className={styles.confirmLabel}>Revoke this key?</span>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setRevokeConfirmId(null)}
                                disabled={isRevoking}
                              >
                                No
                              </Button>
                              <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                loading={isRevoking}
                                loadingText="..."
                                onClick={() =>
                                  revokeFetcher.submit(null, {
                                    method: "post",
                                    action: `/organizations/${organization.id}/settings/api-keys/${apiKey.id}/revoke`,
                                  })
                                }
                              >
                                Revoke
                              </Button>
                            </div>
                          ) : (
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              onClick={() => setRevokeConfirmId(apiKey.id)}
                            >
                              Revoke
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Revoked Keys */}
          {revokedKeys.length > 0 && (
            <div className={styles.section}>
              <h2>Revoked Keys</h2>
              <div className={styles.keyList}>
                {revokedKeys.map((apiKey) => (
                  <div key={apiKey.id} className={`${styles.keyCard} ${styles.keyRevoked}`}>
                    <div className={styles.keyInfo}>
                      <div className={styles.keyHeader}>
                        <span className={styles.keyName}>{apiKey.name}</span>
                        <code className={styles.keyPrefix}>{apiKey.keyPrefix}...</code>
                        <span className={styles.revokedBadge}>Revoked</span>
                      </div>
                      <div className={styles.keyMeta}>
                        <span>Created {formatDate(apiKey.createdAt)}</span>
                        {apiKey.revokedAt && <span>Revoked {formatDate(apiKey.revokedAt)}</span>}
                      </div>
                      <div className={styles.keyScopes}>
                        {apiKey.scopes.map((scope) => (
                          <ScopeLabel key={scope} scope={scope} />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* One-time key display modal */}
      {rawKeyDisplay && (
        <KeyDisplayModal rawKey={rawKeyDisplay} onClose={() => setRawKeyDisplay(null)} />
      )}
    </>
  );
}
